import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Save, Plus, X, Package, MapPin, Map, Tag } from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';
import AdminLayout from '../../components/AdminLayout';

export default function AdminCODSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [config, setConfig] = useState({
    enabled: true,
    min_order_value: 0,
    max_order_value: 50000,
    
    // State restrictions
    allowed_states: [],
    blocked_states: [],
    
    // Pincode restrictions
    allowed_pincodes: [],
    blocked_pincodes: [],
    
    // Product restrictions
    allowed_products: [],
    blocked_products: [],
    
    // Category restrictions
    allowed_categories: [],
    blocked_categories: []
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [codResponse, productsResponse, categoriesResponse] = await Promise.all([
        axiosInstance.get(`${API_URL}/api/admin/cod-config`).catch(() => ({ data: config })),
        axiosInstance.get(`${API_URL}/api/products`),
        axiosInstance.get(`${API_URL}/api/categories`)
      ]);
      
      if (codResponse.data) {
        setConfig({
          ...config,
          ...codResponse.data,
          // Ensure all arrays are initialized
          allowed_states: codResponse.data.allowed_states || [],
          blocked_states: codResponse.data.blocked_states || [],
          allowed_pincodes: codResponse.data.allowed_pincodes || [],
          blocked_pincodes: codResponse.data.blocked_pincodes || [],
          allowed_products: codResponse.data.allowed_products || [],
          blocked_products: codResponse.data.blocked_products || [],
          allowed_categories: codResponse.data.allowed_categories || [],
          blocked_categories: codResponse.data.blocked_categories || []
        });
      }
      setProducts(productsResponse.data || []);
      setCategories(categoriesResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load COD settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axiosInstance.post(`${API_URL}/api/admin/cod-config`, config);
      toast.success('COD settings saved successfully!');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save COD settings');
    } finally {
      setSaving(false);
    }
  };

  const addToList = (field, value) => {
    if (value && !config[field].includes(value)) {
      setConfig({ ...config, [field]: [...config[field], value] });
    }
  };

  const removeFromList = (field, value) => {
    setConfig({ ...config, [field]: config[field].filter(item => item !== value) });
  };

  const addBulkPincodes = (pincodes) => {
    const newPincodes = pincodes.split(/[,\n]/).map(p => p.trim()).filter(p => p && !config.allowed_pincodes.includes(p));
    if (newPincodes.length > 0) {
      setConfig({ ...config, allowed_pincodes: [...config.allowed_pincodes, ...newPincodes] });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Cash on Delivery Settings</h1>
            <p className="text-gray-600 mt-1">Configure comprehensive COD restrictions</p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        {/* Basic Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="cod_enabled"
                checked={config.enabled}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <Label htmlFor="cod_enabled" className="font-semibold">
                Enable Cash on Delivery
              </Label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Minimum Order Value (₹)</Label>
                <Input
                  type="number"
                  value={config.min_order_value}
                  onChange={(e) => setConfig({ ...config, min_order_value: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
              <div>
                <Label>Maximum Order Value (₹)</Label>
                <Input
                  type="number"
                  value={config.max_order_value || ''}
                  onChange={(e) => setConfig({ ...config, max_order_value: parseFloat(e.target.value) || null })}
                  placeholder="No limit"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* State Restrictions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Map className="w-5 h-5 mr-2 text-green-600" />
              State Restrictions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Allowed States (Leave empty to allow all)</Label>
              <div className="flex items-center space-x-2 mt-2">
                <select
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  onChange={(e) => {
                    if (e.target.value) {
                      addToList('allowed_states', e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">Select a state</option>
                  {indianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {config.allowed_states.map(state => (
                  <Badge key={state} className="bg-green-100 text-green-800 px-3 py-1">
                    {state}
                    <button
                      onClick={() => removeFromList('allowed_states', state)}
                      className="ml-2 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Blocked States</Label>
              <div className="flex items-center space-x-2 mt-2">
                <select
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  onChange={(e) => {
                    if (e.target.value) {
                      addToList('blocked_states', e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">Select a state</option>
                  {indianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {config.blocked_states.map(state => (
                  <Badge key={state} className="bg-red-100 text-red-800 px-3 py-1">
                    {state}
                    <button
                      onClick={() => removeFromList('blocked_states', state)}
                      className="ml-2 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pincode Restrictions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-green-600" />
              Pincode Restrictions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Allowed Pincodes (comma or newline separated)</Label>
              <Textarea
                placeholder="600001, 600002, 600003&#10;or one per line"
                rows={4}
                onBlur={(e) => {
                  if (e.target.value.trim()) {
                    addBulkPincodes(e.target.value);
                    e.target.value = '';
                  }
                }}
              />
              <div className="flex flex-wrap gap-2 mt-3 max-h-40 overflow-y-auto">
                {config.allowed_pincodes.map(pincode => (
                  <Badge key={pincode} className="bg-green-100 text-green-800">
                    {pincode}
                    <button
                      onClick={() => removeFromList('allowed_pincodes', pincode)}
                      className="ml-2 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Product Restrictions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Package className="w-5 h-5 mr-2 text-green-600" />
              Product Restrictions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Blocked Products (COD not available)</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 mt-2"
                onChange={(e) => {
                  if (e.target.value) {
                    addToList('blocked_products', e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Select a product to block</option>
                {products.map(product => (
                  <option key={product._id || product.id} value={product._id || product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
              <div className="space-y-2 mt-3 max-h-40 overflow-y-auto">
                {config.blocked_products.map(productId => {
                  const product = products.find(p => (p._id || p.id) === productId);
                  return product ? (
                    <div key={productId} className="flex items-center justify-between bg-red-50 px-3 py-2 rounded border border-red-200">
                      <span className="text-sm">{product.name}</span>
                      <button
                        onClick={() => removeFromList('blocked_products', productId)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Category Restrictions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Tag className="w-5 h-5 mr-2 text-green-600" />
              Category Restrictions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Blocked Categories (COD not available)</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 mt-2"
                onChange={(e) => {
                  if (e.target.value) {
                    addToList('blocked_categories', e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Select a category to block</option>
                {categories.map(category => (
                  <option key={category._id || category.id} value={category._id || category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <div className="flex flex-wrap gap-2 mt-3">
                {config.blocked_categories.map(categoryId => {
                  const category = categories.find(c => (c._id || c.id) === categoryId);
                  return category ? (
                    <Badge key={categoryId} className="bg-red-100 text-red-800 px-3 py-1">
                      {category.name}
                      <button
                        onClick={() => removeFromList('blocked_categories', categoryId)}
                        className="ml-2 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ) : null;
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-blue-900 mb-3">Configuration Summary</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li>✓ COD is {config.enabled ? 'enabled' : 'disabled'}</li>
              <li>✓ Order value: ₹{config.min_order_value} - ₹{config.max_order_value || '∞'}</li>
              <li>✓ {config.allowed_states.length > 0 ? `${config.allowed_states.length} states allowed` : 'All states allowed'}</li>
              <li>✓ {config.blocked_states.length} states blocked</li>
              <li>✓ {config.allowed_pincodes.length} pincodes allowed</li>
              <li>✓ {config.blocked_products.length} products blocked from COD</li>
              <li>✓ {config.blocked_categories.length} categories blocked from COD</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
